import PropTypes from 'prop-types';
import React, { Component, Children, cloneElement } from 'react';
import lodash from 'lodash';
import { isIE11 } from 'common/util/mobile';
import styles from 'common/components/layout/GridPanel.scss';

const gridTrackPropType = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
]);

export const gridProperties = PropTypes.shape({
    /**
     * display: determines if this is a grid or inline grid
     */
    display: PropTypes.oneOf(['grid', 'inline-grid']),
    /**
     * gridRows: determines the grid-template-rows (-ms-grid-rows) property
     * can either be a string, a number, or an array of strings or numbers
     * strings are interpreted literally. numbers are interpreted in fr units.
     * arrays are concatenated element by element.
     */
    gridRows: gridTrackPropType,
    /**
     * gridColumns: determines the grid-template-columns (-ms-grid-columns) property
     * can either be a string, a number, or an array of strings or numbers
     * strings are interpreted literally. numbers are interpreted in fr units.
     * arrays are concatenated element by element
     */
    gridColumns: gridTrackPropType,
    /**
     * rowItemAlignment: determines the horizontal alignment of the individual grid panels
     */
    rowItemAlignment: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /**
     * rowColumnAlignment: determines the horizontal alignment of the grid as a whole. not available in IE
     */
    rowContentAlignment: PropTypes.oneOf(['start', 'center', 'end', 'stretch', ' space-around', 'space-between']),
    /**
     * columnItemAlignment: determines the vertical alignment of the individual grid panels
     */
    columnItemAlignment: PropTypes.oneOf(['start', 'center', 'end', 'stretch']),
    /**
     * columnColumnAlignment: determines the vertical alignment of the grid as a whole. not available in IE
     */
    columnContentAlignment: PropTypes.oneOf(['start', 'center', 'end', 'stretch', ' space-around', 'space-between']),
    /**
     * className: class that is applied to the grid container (div element)
     */
    className: PropTypes.string,
    /**
     * containerProps: props that are rendered onto the grid container (div element)
     */
    containerProps: PropTypes.object,
});

const gridDefaultProps = {
    display: 'grid',
    gridColumns: '1fr',
    gridRows: '1fr',
    rowItemAlignment: 'stretch',
    rowContentAlignment: 'start',
    columnItemAlignment: 'stretch',
    columnContentAlignment: 'start',
};

export const gridPanelProperties = PropTypes.shape({
    /**
     * gridRow: this is the grid row that the grid panel will appear in
     */
    gridRow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * gridRowSpan: this is the number of cells that will be taken up horizontally by the grid panel
     */
    gridRowSpan: PropTypes.number,
    /**
     * gridColumn: this is the grid column that the grid panel will appear
     */
    gridColumn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * gridColumnSpan: this is the number of cells that will be taken up vertically by the grid panel
     */
    gridColumnSpan: PropTypes.number,
    /**
     * rowAlign: this will determine the horizontal alignment of the items inside of the grid panel
     */
    rowAlign: PropTypes.oneOf(['flex-start', 'center', 'flex-end', 'stretch']),
    /**
     * columnAlign: this will determine the vertical alignment of the items inside of the grid panel
     */
    columnAlign: PropTypes.oneOf(['flex-start', 'center', 'flex-end', 'stretch']),
    /**
     * className: additional className that is added onto the grid panel (div element)
     */
    className: PropTypes.string,
    /**
     * containerProps: additional react props that are added onto the grid panel (div element)
     */
    containerProps: PropTypes.object,
});

const gridPanelDefaultProps = {
    gridRow: 1,
    gridRowSpan: 1,
    gridColumn: 1,
    gridColumnSpan: 1,
};

/**
 * Interprets a grid track prop type.
 * @param track
 * @returns {string}
 */
function interpretGridTrack(track) {
    if (lodash.isArray(track)) {
        return lodash.map(track, interpretGridTrack).join(' ');
    }
    if (typeof track === 'number') {
        return `${track}fr`;
    }
    return track;
}

class StandardGrid extends Component {
    /**
     * Provide a display prop of grid or display
     * @returns {string}
     */
    get display() {
        return this.props.display;
    }

    /**
     * Takes the gridRows prop and interprets it as a grid-template-rows compatible style
     * A template row can be a string, a number, or an array of strings and/or numbers.
     *
     * For strings, the value is passed in unaltered.
     * For numbers, the value is interpreted as a string with the 'fr' unit added to it.
     * For arrays, each value is interpreted as a string or number,
     *  and then concatenated together with a space in between each item
     * @returns {string}
     */
    get rows() {
        return interpretGridTrack(this.props.gridRows);
    }

    /**
     * Takes the gridColumns prop and interprets it as a grid-template-columns compatible style
     * A template column can be a string, a number, or an array of strings and/or numbers.
     *
     * For strings, the value is passed in unaltered.
     * For numbers, the value is interpreted as a string with the 'fr' unit added to it.
     * For arrays, each value is interpreted as a string or number,
     *  and then concatenated together with a space in between each item
     * @returns {string}
     */
    get columns() {
        return interpretGridTrack(this.props.gridColumns);
    }

    /**
     * Aligns the individual cells according to the row (horizontal) axis
     * @returns {string}
     */
    get rowItemAlignment() {
        return this.props.rowItemAlignment;
    }

    /**
     * Aligns the entire grid according to the row (horizontal) axis if there is
     * extra space in the parent container
     * @returns {string}
     */
    get rowContentAlignment() {
        return this.props.rowContentAlignment;
    }

    /**
     * Aligns the individual grid cells according to the column (vertical) axis
     * @returns {string}
     */
    get columnItemAlignment() {
        return this.props.columnItemAlignment;
    }

    /**
     * Aligns the entire grid according to the column (vertical) axis if there is
     * extra space in the parent container
     * @returns {string}
     */
    get columnContentAlignment() {
        return this.props.columnContentAlignment;
    }

    /**
     * Additional className that is set on the container property
     */
    get className() {
        return this.props.className || '';
    }

    /**
     * Additional properties that are defined on the container
     * @returns {*}
     */
    get containerProps() {
        return this.props.containerProps;
    }

    /**
     * This should be an array of just GridPanel elements.
     */
    get children() {
        return this.props.children;
    }

    /**
     * Returns the grid properties for rendering as an inline style.
     * @returns {{display: StandardGrid.display, gridRows: StandardGrid.rows, gridColumns: StandardGrid.columns, justifyItems: StandardGrid.rowItemAlignment, justifyContent: StandardGrid.rowContentAlignment, alignItems: StandardGrid.columnItemAlignment, alignContent: StandardGrid.columnContentAlignment}}
     */
    get gridStyles() {
        const {
            display,
            rows,
            columns,
            rowItemAlignment,
            rowContentAlignment,
            columnItemAlignment,
            columnContentAlignment,
        } = this;
        return {
            display: display,
            gridTemplateRows: rows,
            gridTemplateColumns: columns,
            justifyItems: rowItemAlignment,
            justifyContent: rowContentAlignment,
            alignItems: columnItemAlignment,
            alignContent: columnContentAlignment,
        };
    }

    render() {
        const {
            className,
            containerProps,
            children,
            gridStyles,
        } = this;
        return (
            <div
                className={className}
                {...containerProps}
                style={gridStyles}>
                {children}
            </div>
        );
    }
}

StandardGrid.propTypes = gridProperties;
StandardGrid.defaultProps = gridDefaultProps;


class IE11Grid extends StandardGrid {

    /**
     * This should be an array of just GridPanel elements.
     *
     * Note: the reason for the difference here and the parent class is because IE11 does not
     * support the alignItems, alignContent, justifyItems, and justifyContent property.
     *
     * The alignContent and justifyContent properties are unsalvageable in a straightforward way
     * and are omitted
     *
     * The alignItems and justifyItems properties are faked by passing in the rowAlign and
     * columnAlign properties to gridPanels, which render using the justifySelf and alignSelf
     * properties of IE. These properties do not exist as-is, and instead exist as
     * -ms-grid-column-align (=alignSelf) and -ms-grid-row-align (=justifySelf).
     */
    get children() {
        const { rowItemAlignment, columnItemAlignment } = this;
        return Children.map(this.props.children, child => {
            return cloneElement(child, {
                rowAlign: rowItemAlignment,
                columnAlign: columnItemAlignment,
                ...child.props
            });
        });
    }

    /**
     * Returns the grid properties for rendering as an inline style.
     * @returns {{display: StandardGrid.display, gridRows: StandardGrid.rows, gridColumns: StandardGrid.columns, justifyItems: StandardGrid.rowItemAlignment, justifyContent: StandardGrid.rowContentAlignment, alignItems: StandardGrid.columnItemAlignment, alignContent: StandardGrid.columnContentAlignment}}
     */
    get gridStyles() {
        const {
            display,
            rows,
            columns,
            rowItemAlignment,
            columnItemAlignment,
            rowContentAlignment,
            columnContentAlignment,
        } = this;
        return {
            display: '-ms-' + display,
            msGridRows: rows,
            msGridColumns: columns,
            justifyItems: rowItemAlignment,
            justifyContent: rowContentAlignment,
            alignItems: columnItemAlignment,
            alignContent: columnContentAlignment,
        };
    }
}

IE11Grid.propTypes = gridProperties;
IE11Grid.defaultProps = gridDefaultProps;

class StandardGridPanel extends Component {

    /**
     * Returns the row and row span for this grid panel for use with the grid-row css property
     * @returns {string}
     */
    get row() {
        const { gridRow, gridRowSpan } = this.props;
        return `${gridRow} / span ${gridRowSpan}`;
    }

    /**
     * Returns the row and row span for this grid panel for use with the grid-column css property
     * @returns {string}
     */
    get column() {
        const { gridColumn, gridColumnSpan } = this.props;
        return `${gridColumn} / span ${gridColumnSpan}`;
    }

    /**
     * Returns the horizontal alignment for the content of the grid panel
     * @returns {string}
     */
    get rowAlign() {
        return this.props.rowAlign;
    }

    /**
     * Returns the vertical alignment for the content of the grid panel
     * @returns {string}
     */
    get columnAlign() {
        return this.props.columnAlign;
    }

    /**
     * Returns the additional class name provided to the grid panel
     */
    get className() {
        return [
            styles.gridPanel,
            this.props.className || '',
        ].join(' ');
    }

    /**
     * Returns the additional properties that are rendered onto the grid panel
     * @returns {*}
     */
    get containerProps() {
        return this.props.containerProps;
    }

    /**
     * Returns the content of the grid panel
     */
    get content() {
        return this.props.children;
    }

    /**
     * Returns the style property used for rendering
     * @returns {{gridRow: StandardGridPanel.row, gridColumn: StandardGridPanel.column, alignItems: StandardGridPanel.columnAlign, justifyContent: StandardGridPanel.rowAlign}}
     */
    get gridPanelStyles() {
        const {
            row,
            column,
            rowAlign,
            columnAlign,
        } = this;
        return {
            gridRow: row,
            gridColumn: column,
            alignItems: columnAlign,
            justifyContent: rowAlign,
        };
    }

    render() {
        const {
            className,
            containerProps,
            gridPanelStyles,
            content,
        } = this;
        return (
            <div
                className={className}
                {...containerProps}
                style={gridPanelStyles}>
                {content}
            </div>
        );
    }
}

StandardGridPanel.propTypes = gridPanelProperties;
StandardGridPanel.defaultProps = gridPanelDefaultProps;

class IE11GridPanel extends StandardGridPanel {
    /**
     * Returns the row for this grid panel for use with the -ms-grid-row css property
     * @returns {string}
     */
    get row() {
        return this.props.gridRow;
    }

    /**
     * Returns the row for this grid panel for use with the -ms-grid-row-span css property
     * @returns {string}
     */
    get rowSpan() {
        return this.props.gridRowSpan;
    }

    /**
     * Returns the column for this grid panel for use with the -ms-grid-column css property
     * @returns {string}
     */
    get column() {
        return this.props.gridColumn;
    }

    /**
     * Returns the column for this grid panel for use with the -ms-grid-column-span css property
     * @returns {string}
     */
    get columnSpan() {
        return this.props.gridColumnSpan;
    }

    /**
     * Returns the style property used for rendering
     * @returns {{msGridRow: IE11GridPanel.row, msGridRowSpan: IE11GridPanel.rowSpan, msGridColumn: IE11GridPanel.column, msGridColumnSpan: IE11GridPanel.columnSpan, alignItems: IE11GridPanel.columnAlign, justifyContent: IE11GridPanel.rowAlign}}
     */
    get gridPanelStyles() {
        const {
            row,
            column,
            rowSpan,
            columnSpan,
            rowAlign,
            columnAlign,
        } = this;
        return {
            msGridRow: row,
            msGridRowSpan: rowSpan,
            msGridColumn: column,
            msGridColumnSpan: columnSpan,
            alignItems: columnAlign,
            justifyContent: rowAlign,
        };
    }
}

IE11GridPanel.propTypes = gridPanelProperties;
IE11GridPanel.defaultProps = gridPanelDefaultProps;


/**
 * Renders a grid according to the CSS panel spec. This is intended to be used with the
 * GridPanel component defined below to produce grids in our application for layout purposes.
 * <Grid gridRows="1fr 1fr" gridColumns="1fr 1fr">
 *      <GridPanel gridRow="1" gridColumn="2">
 *          { content of panel 1 }
 *      </GridPanel>
 *      <GridPanel gridRow="2" gridColumn="1" gridColumnSpan="2">
 *          { content of panel 2 }
 *      </GridPanel>
 * </Grid>
 */
export const Grid = isIE11() ? IE11Grid : StandardGrid;

/**
 * Renders an individual grid panel as a child element of the grid component above
 * with a given row and column position, given by the gridRow and gridColumn attribute.
 * These values must be specified in order for the grid to appear properly in IE.
 */
export const GridPanel = isIE11() ? IE11GridPanel : StandardGridPanel;
